import { TFunction } from "i18next";

import {
  API_DATE_FORMAT,
  API_DATE_FORMAT_W_TIME,
  HOUR_ONLY,
  MONTH_DAY_NUMERIC,
  MONTH_ONLY,
  YEAR_ONLY,
} from "shared/constants";
import { formatDate } from "shared/locale/format";
import { capitalizeFirstLetter, randomID } from "shared/utils";

import { getLastDateFilterLabelString } from "pages/Dashboard/utils";
import {
  AnyDateDropdownOptions,
  DateDropdownOptionDay,
  DateDropdownOptionMonth,
  DateDropdownOptionWeek,
  DateDropdownOptionYear,
} from "pages/shared/Dashboard/types";
import {
  isDateDropdownOptionDay,
  isDateDropdownOptionInLast,
  isDateDropdownOptionMonth,
  isDateDropdownOptionWeek,
  isDateDropdownOptionYear,
} from "pages/SuperAdmin/shared/DateConfig/utils";

import Typography from "features/components/Typography";
import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";

export const getDateDropdownOptionText = (
  option: AnyDateDropdownOptions,
  t: TFunction
): string => {
  if (isDateDropdownOptionInLast(option)) {
    return getLastDateFilterLabelString(option.value, t, "in_last");
  }

  if (isDateDropdownOptionWeek(option)) {
    if (option.offset === 0) {
      return t("common:relative_date.this_week");
    } else if (option.offset === 1) {
      return t("common:relative_date.last_week");
    } else {
      return t("common:relative_date.weeks_ago", { count: option.offset });
    }
  }

  if (isDateDropdownOptionMonth(option)) {
    if (option.offset === 0) {
      return t("common:relative_date.this_month");
    } else if (option.offset === 1) {
      return t("common:relative_date.last_month");
    } else {
      return t("common:relative_date.months_ago", { count: option.offset });
    }
  }

  if (isDateDropdownOptionYear(option)) {
    if (option.offset === 0) {
      return t("common:relative_date.this_year");
    } else if (option.offset === 1) {
      return t("common:relative_date.last_year");
    } else {
      return t("common:relative_date.years_ago", { count: option.offset });
    }
  }

  if (isDateDropdownOptionDay(option)) {
    if (option.offset === 0) {
      return t("common:relative_date.today");
    } else if (option.offset === 1) {
      return t("common:relative_date.yesterday");
    } else {
      return t("common:relative_date.days_ago", { count: option.offset });
    }
  }

  return t("common:not_available");
};

export const getDateDropdownOptionLabel = (
  option: AnyDateDropdownOptions,
  t: TFunction
): JSX.Element => {
  if (isDateDropdownOptionInLast(option)) {
    return (
      <Typography variant="body2">
        {capitalizeFirstLetter(getDateDropdownOptionText(option, t))}
      </Typography>
    );
  } else if (isDateDropdownOptionWeek(option)) {
    const { startDate } = getWeekOptionStartEndDate(option);
    const mainText = capitalizeFirstLetter(
      getDateDropdownOptionText(option, t)
    );

    if (option.offset === 0) {
      const formattedDate = formatDate(startDate.getTime(), MONTH_DAY_NUMERIC);

      return (
        <span>
          <Typography variant="body2" component="span">
            {mainText}{" "}
          </Typography>
          <Typography variant="caption" component="span">
            {t("common:relative_date.range_to_latest", {
              start: `${formattedDate} ${getTimeStringWithTimezone(startDate)}`,
            })}
          </Typography>
        </span>
      );
    } else if (option.offset > 0) {
      const { startDate, endDate } = getWeekOptionStartEndDate(option);

      const lastSundayFormatted = formatDate(
        startDate.getTime(),
        MONTH_DAY_NUMERIC
      );
      const currentSundayFormatted = formatDate(
        endDate.getTime(),
        MONTH_DAY_NUMERIC
      );

      return (
        <span>
          <Typography variant="body2" component="span">
            {mainText}{" "}
          </Typography>
          <Typography
            variant="caption"
            component="span"
          >{`(${lastSundayFormatted} ${getTimeStringWithTimezone(startDate)} - ${currentSundayFormatted} ${getTimeStringWithTimezone(endDate)})`}</Typography>
        </span>
      );
    }
  } else if (isDateDropdownOptionMonth(option)) {
    const { startDate } = getMonthOptionStartEndDate(option);
    const lastMonthName = formatDate(startDate.getTime(), MONTH_ONLY);
    const mainText = capitalizeFirstLetter(
      getDateDropdownOptionText(option, t)
    );

    if (option.offset === 0) {
      return (
        <span>
          <Typography variant="body2" component="span">
            {mainText}{" "}
          </Typography>
          <Typography
            variant="caption"
            component="span"
          >{`(${lastMonthName})`}</Typography>
        </span>
      );
    } else if (option.offset === 1) {
      return (
        <span>
          <Typography variant="body2" component="span">
            {mainText}{" "}
          </Typography>
          <Typography
            variant="caption"
            component="span"
          >{`(${lastMonthName})`}</Typography>
        </span>
      );
    } else {
      return (
        <span>
          <Typography variant="body2" component="span">
            {mainText}{" "}
          </Typography>
          <Typography
            variant="caption"
            component="span"
          >{`(${lastMonthName})`}</Typography>
        </span>
      );
    }
  } else if (isDateDropdownOptionYear(option)) {
    const { startDate } = getYearOptionStartEndDate(option);
    const yearName = formatDate(startDate.getTime(), YEAR_ONLY);
    const mainText = capitalizeFirstLetter(
      getDateDropdownOptionText(option, t)
    );

    return (
      <span>
        <Typography variant="body2" component="span">
          {mainText}{" "}
        </Typography>
        <Typography
          variant="caption"
          component="span"
        >{`(${yearName})`}</Typography>
      </span>
    );
  } else if (isDateDropdownOptionDay(option)) {
    const { startDate, endDate } = getDayOptionStartEndDate(option);
    const formattedStartDate = formatDate(
      startDate.getTime(),
      MONTH_DAY_NUMERIC
    );
    const formattedEndDate = formatDate(endDate.getTime(), MONTH_DAY_NUMERIC);
    const labelText = capitalizeFirstLetter(
      getDateDropdownOptionText(option, t)
    );

    return (
      <span>
        <Typography variant="body2" component="span">
          {labelText}{" "}
        </Typography>
        <Typography
          variant="caption"
          component="span"
        >{`(${formattedStartDate} ${getTimeStringWithTimezone(startDate)} - ${
          option.offset === 0
            ? t("common:relative_date.now")
            : `${formattedEndDate} ${getTimeStringWithTimezone(endDate)}`
        })`}</Typography>
      </span>
    );
  }

  return <Typography variant="body2">{t("common:not_available")}</Typography>;
};

const getTimeStringWithTimezone = (date: Date): string =>
  formatDate(date.getTime(), HOUR_ONLY).replace(" ", "").toLowerCase();

/**
 * `daysToCutoff` is how far back the current period's cutoff date sits: 0 for a
 * day, the days since the cutoff weekday for a week. A cutoff still ahead of
 * `now` belongs to the next period, so the window steps back one more. The
 * current period ends at `now` rather than at its next cutoff, matching the
 * month option and the "now" the label shows.
 *
 * Each boundary steps whole days from `now` and applies the cutoff hour last.
 * Applying it once up front and then stepping would carry a spring-forward
 * hour that does not exist on one day into every earlier period.
 */
const getCutoffAnchoredStartEndDate = (
  now: Date,
  cutoffHour: number,
  daysToCutoff: number,
  offset: number,
  periodDays: number
) => {
  const cutoffDaysBack = (daysBack: number) => {
    const date = new Date(now);
    date.setDate(now.getDate() - daysBack);
    date.setHours(cutoffHour, 0, 0, 0);

    return date;
  };

  const periodsBack = offset + (cutoffDaysBack(daysToCutoff) > now ? 1 : 0);
  const startDate = cutoffDaysBack(daysToCutoff + periodsBack * periodDays);

  if (offset === 0) return { startDate, endDate: new Date(now) };

  return {
    startDate,
    endDate: cutoffDaysBack(daysToCutoff + (periodsBack - 1) * periodDays),
  };
};

const getWeekOptionStartEndDate = (option: DateDropdownOptionWeek) => {
  const now = new Date();
  const cutoffDay = option.cutoffDay ?? 0;
  // Days back to the most recent occurrence of cutoffDay on or before today.
  const daysSinceCutoff = (now.getDay() - cutoffDay + 7) % 7;

  return getCutoffAnchoredStartEndDate(
    now,
    option.hour ?? 17, // Default to 5 PM ET
    daysSinceCutoff,
    option.offset,
    7
  );
};

const getMonthOptionStartEndDate = (option: DateDropdownOptionMonth) => {
  const now = new Date();

  const startDate = new Date(
    now.getFullYear(),
    now.getMonth() - option.offset,
    1,
    0,
    0,
    0,
    0
  );

  const endDateOffset = new Date(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
  const endDate = option.offset === 0 ? now : endDateOffset;

  return { startDate, endDate };
};

const getYearOptionStartEndDate = (option: DateDropdownOptionYear) => {
  const now = new Date();

  const startDate = new Date(
    now.getFullYear() - option.offset,
    0,
    1,
    0,
    0,
    0,
    0
  );

  const endDate =
    option.offset === 0
      ? now
      : new Date(startDate.getFullYear(), 11, 31, 23, 59, 59, 999);

  return { startDate, endDate };
};

const getDayOptionStartEndDate = (option: DateDropdownOptionDay) =>
  getCutoffAnchoredStartEndDate(
    new Date(),
    option.hour ?? 0,
    0,
    option.offset,
    1
  );

export const convertDateDropdownOptionToDateRangeFilter = (
  option: AnyDateDropdownOptions,
  fieldName: string,
  isDateTime: boolean = true,
  dateTimeFormat: string = API_DATE_FORMAT_W_TIME
): FilterRowState => {
  if (isDateDropdownOptionInLast(option)) {
    return {
      type: "row",
      id: `row-${randomID()}`,
      attribute: fieldName,
      operator: FilterOperator.IN_LAST,
      values: [option.value],
    };
  } else if (isDateDropdownOptionWeek(option)) {
    const { startDate, endDate } = getWeekOptionStartEndDate(option);

    return {
      type: "row",
      id: `row-${randomID()}`,
      attribute: fieldName,
      operator: FilterOperator.BETWEEN,
      values: [
        formatDate(
          startDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
        formatDate(
          endDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
      ],
    };
  } else if (isDateDropdownOptionMonth(option)) {
    const { startDate, endDate } = getMonthOptionStartEndDate(option);

    return {
      type: "row",
      id: `row-${randomID()}`,
      attribute: fieldName,
      operator: FilterOperator.BETWEEN,
      values: [
        formatDate(startDate.toISOString(), API_DATE_FORMAT),
        formatDate(endDate.toISOString(), API_DATE_FORMAT),
      ],
    };
  } else if (isDateDropdownOptionYear(option)) {
    const { startDate, endDate } = getYearOptionStartEndDate(option);

    return {
      type: "row",
      id: `row-${randomID()}`,
      attribute: fieldName,
      operator: FilterOperator.BETWEEN,
      values: [
        formatDate(
          startDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
        formatDate(
          endDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
      ],
    };
  } else if (isDateDropdownOptionDay(option)) {
    const { startDate, endDate } = getDayOptionStartEndDate(option);

    return {
      type: "row",
      id: `row-${randomID()}`,
      attribute: fieldName,
      operator: FilterOperator.BETWEEN,
      values: [
        formatDate(
          startDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
        formatDate(
          endDate.toISOString(),
          !isDateTime ? API_DATE_FORMAT : dateTimeFormat
        ),
      ],
    };
  }

  return {
    type: "row",
    id: `row-${randomID()}`,
    attribute: fieldName,
    operator: FilterOperator.NOT_FILTERED,
    values: [],
  };
};
